import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Mail, Eye, MousePointer, AlertTriangle, Search, RefreshCw, X, ExternalLink, Send, TestTube, Bell } from 'lucide-react';
import api from '../../services/api';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import CampaignList from './Campaigns/CampaignList';
import CampaignCompose from './Campaigns/CampaignCompose';
import CampaignDetail from './Campaigns/CampaignDetail';

// Email type display mapping for consistent frontend display
const EMAIL_TYPE_DISPLAY: Record<string, string> = {
  ORDER_CONFIRMED: 'Order Confirmed',
  ORDER_IN_PRODUCTION: 'Order In Production',
  ORDER_PACKAGED: 'Order Packaged',
  ORDER_SHIPPED: 'Order Shipped',
  ORDER_READY_FOR_PICKUP: 'Ready for Pickup',
  ORDER_DELIVERED: 'Order Delivered',
  INVOICE_SENT: 'Invoice Sent',
  PAYMENT_RECEIVED: 'Payment Received',
  PAYMENT_REMINDER: 'Payment Reminder',
  REGISTRATION_APPROVED: 'Registration Approved',
  REGISTRATION_REJECTED: 'Registration Rejected',
  WELCOME: 'Welcome',
  PASSWORD_RESET: 'Password Reset',
  MONDAY_DIGEST: 'Monday Digest',
  TAX_CERT_EXPIRING: 'Tax Cert Expiring',
  // Team notification emails
  TEAM_NEW_ORDER: 'Team: New Order',
  TEAM_NEW_REGISTRATION: 'Team: New Registration',
  TEAM_TAX_CERT_EXPIRING: 'Team: Tax Cert Expiring',
  TEAM_LARGE_ORDER: 'Team: Large Order',
  TEAM_ORDER_MODIFICATION: 'Team: Order Modified',
  MARKETING: 'Marketing',
  // Store Club email types
  STORE_CLUB_WELCOME: 'Store Club: Welcome',
  STORE_CLUB_ORDER_CONFIRMED: 'Store Club: Order Confirmed',
  STORE_CLUB_LOCKER_READY: 'Store Club: Locker Ready',
  STORE_CLUB_ORDER_READY: 'Store Club: Order Ready',
  STORE_CLUB_FEED_REMINDER: 'Store Club: Feed Reminder',
  STORE_CLUB_WEATHER_ALERT: 'Store Club: Weather Alert',
  // Planner/Farmhand emails
  PLANNER_VERIFICATION: 'Planner: Order Verification',
  PLANNER_FORECAST: 'Planner: Weekly Forecast',
  PLANNER_URGENT_ALERT: 'Planner: Urgent Alert',
  // Milk Run / Freight emails
  MILK_RUN_QUOTE_REQUEST: 'Milk Run: Quote Request',
  MILK_RUN_BOOKING_CONFIRM: 'Milk Run: Booking Confirmed',
  MILK_RUN_QUOTE_RECEIVED: 'Milk Run: Quote Received',
  // System emails
  TEST_EMAIL: 'Test Email',
  OTHER: 'Other',
};

// Helper to get email type display name
const getEmailTypeDisplay = (type: string, backendDisplay?: string): string => {
  return EMAIL_TYPE_DISPLAY[type] || backendDisplay || type;
};

interface Email {
  id: number;
  email_type: string;
  email_type_display: string;
  recipient_email: string;
  subject: string;
  status: string;
  status_display: string;
  sent_at: string;
  opened_at: string | null;
  opened_count: number;
  clicked_at: string | null;
  clicked_count: number;
  customer_id: number | null;
  customer_name: string | null;
  order_id: number | null;
  order_number: string | null;
  sent_by: string | null;
}

// Clicked link can be a string URL or an object from webhook
type ClickedLink = string | { url: string; clicked_at?: string };

interface EmailDetail extends Email {
  error_message: string | null;
  clicked_links: ClickedLink[];
  delivered_at: string | null;
  bounced_at: string | null;
  bounce_type: string | null;
  unsubscribed_at: string | null;
  html_content: string | null;
  template_context: Record<string, any>;
  mailgun_message_id: string | null;
}

interface Analytics {
  totals: {
    all_time: number;
    last_30_days: number;
    last_7_days: number;
  };
  rates: {
    open_rate: number;
    click_rate: number;
    bounce_rate: number;
  };
  status_breakdown: Record<string, number>;
  emails_by_type: Array<{ type: string; type_display: string; count: number }>;
  daily_volume: Array<{ date: string; count: number }>;
  recent_activity: Array<{ id: number; type: string; recipient: string; status: string; sent_at: string }>;
}

interface FilterOptions {
  email_types: Array<{ value: string; label: string }>;
  statuses: Array<{ value: string; label: string }>;
}

const EmailPortal: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [resending, setResending] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Test email state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Alert testing state
  const [showAlertTesting, setShowAlertTesting] = useState(false);
  const [alertTestEmail, setAlertTestEmail] = useState('');
  const [alertType, setAlertType] = useState<'feed_bin' | 'weather'>('feed_bin');
  const [sendingAlertTest, setSendingAlertTest] = useState(false);
  const [alertTestResult, setAlertTestResult] = useState<string | null>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<'dashboard' | 'campaigns' | 'log'>('dashboard');
  const [campaignView, setCampaignView] = useState<'list' | 'compose' | 'detail'>('list');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  // Filters
  // Server search: the input binds to `searchInput` (instant); the debounced `search`
  // (400ms) is what loadEmails queries on, so typing fires one request after the pause.
  const { value: searchInput, setValue: setSearchInput, debounced: search } = useDebouncedSearch('', 400);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/admin/emails/analytics/');
      setAnalytics(response.data);
    } catch (err: any) {
      console.error('[EmailPortal] Failed to load analytics:', err);
    }
  }, []);

  const loadEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (typeFilter) params.append('email_type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', page.toString());

      const response = await api.get(`/admin/emails/?${params.toString()}`);
      setEmails(response.data.results);
      setTotalPages(Math.ceil(response.data.count / 50));
    } catch (err: any) {
      console.error('[EmailPortal] Failed to load emails:', err);
      setError('Failed to load emails');
    }
  }, [search, typeFilter, statusFilter, page]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/admin/emails/options/');
      setFilterOptions(response.data);
    } catch (err: any) {
      console.error('[EmailPortal] Failed to load filter options:', err);
    }
  }, []);

  // Initial page load ONLY: analytics + filter options + first page of emails, behind
  // the full-page `loading` spinner. `loadEmails` is intentionally NOT a dependency —
  // a search changes its identity, and we must not re-run this (which would re-blank the
  // page and reload the charts). Runs exactly once on mount.
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadAnalytics(), loadEmails(), loadFilterOptions()]);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload ONLY the email list when search / filters / page change (debounced search =
  // once per pause). Skips the mount run (handled above, so no double fetch) and never
  // touches the page-level `loading`, so the search box + list stay mounted while typing
  // — no reset, no spinner swap. Matches Store Inventory's feel.
  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (!didInitialLoad.current) { didInitialLoad.current = true; return; }
    loadEmails();
  }, [search, typeFilter, statusFilter, page, loadEmails]);

  const handleViewEmail = async (emailId: number) => {
    try {
      const response = await api.get(`/admin/emails/${emailId}/`);
      setSelectedEmail(response.data);
      setShowModal(true);
    } catch (err: any) {
      console.error('[EmailPortal] Failed to load email detail:', err);
      alert('Failed to load email details');
    }
  };

  const handleResend = async () => {
    if (!selectedEmail) return;
    if (!selectedEmail.html_content) {
      alert('Cannot resend: Email content not stored (older email)');
      return;
    }
    if (!window.confirm(`Resend this email to ${selectedEmail.recipient_email}?`)) return;

    console.log('[EmailPortal] Resending email:', selectedEmail.id, selectedEmail.subject);
    setResending(true);
    try {
      const response = await api.post(`/admin/emails/${selectedEmail.id}/resend/`);
      console.log('[EmailPortal] Resend response:', response.data);
      if (response.data.success) {
        alert(`Email resent to ${selectedEmail.recipient_email}`);
        loadEmails();
        setShowModal(false);
      } else {
        alert(`Failed to resend: ${response.data.error}`);
      }
    } catch (err: any) {
      console.error('[EmailPortal] Resend error:', err.response?.status, err.response?.data);
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to resend email';
      alert(errorMsg);
    } finally {
      setResending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setSendingTestEmail(true);
    try {
      const response = await api.post('/admin/test-email/', { email: testEmail });
      if (response.data.success) {
        alert(`Test email sent to ${testEmail}`);
        setShowTestEmailModal(false);
        setTestEmail('');
        loadEmails(); // Refresh to show the test email
      } else {
        alert(`Failed: ${response.data.error}`);
      }
    } catch (err: any) {
      console.error('[EmailPortal] Test email error:', err);
      alert(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSendAlertTest = async () => {
    if (!alertTestEmail || !alertTestEmail.includes('@')) {
      setAlertTestResult('Please enter a valid email address');
      return;
    }

    setSendingAlertTest(true);
    setAlertTestResult(null);
    try {
      const response = await api.post('/admin/alerts/test/', {
        alert_type: alertType,
        mode: 'send_test',
        test_email: alertTestEmail
      });
      if (response.data.emails_sent > 0) {
        setAlertTestResult(`✅ Test ${alertType === 'feed_bin' ? 'Feed Bin' : 'Weather'} alert sent to ${alertTestEmail}`);
      } else if (response.data.members_affected?.length === 0) {
        setAlertTestResult(`⚠️ No members would receive this alert type currently. Test email sent anyway.`);
      } else {
        setAlertTestResult(`✅ Test sent to ${alertTestEmail}`);
      }
    } catch (err: any) {
      console.error('[EmailPortal] Alert test error:', err);
      setAlertTestResult(`❌ Failed: ${err.response?.data?.error || 'Could not send test alert'}`);
    } finally {
      setSendingAlertTest(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      QUEUED: '#9e9e9e',
      SENT: '#2196f3',
      DELIVERED: '#4caf50',
      OPENED: '#9c27b0',
      CLICKED: '#00bcd4',
      BOUNCED: '#ff9800',
      FAILED: '#f44336',
      UNSUBSCRIBED: '#795548',
      COMPLAINED: '#d32f2f',
      SKIPPED: '#607d8b',
    };
    return colors[status] || '#9e9e9e';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'OPENED': return <Eye size={14} />;
      case 'CLICKED': return <MousePointer size={14} />;
      case 'BOUNCED':
      case 'FAILED': return <AlertTriangle size={14} />;
      default: return null;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHrs = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffHrs < 1) return `${Math.round(diffMs / 60000)} min ago`;
    if (diffHrs < 24) return `${Math.round(diffHrs)} hrs ago`;
    if (diffDays < 7) return `${Math.round(diffDays)} days ago`;
    return date.toLocaleDateString();
  };

  const truncateEmail = (email: string, maxLen: number = 25) => {
    if (email.length <= maxLen) return email;
    return email.substring(0, maxLen) + '...';
  };

  const truncateSubject = (subject: string, maxLen: number = 40) => {
    if (subject.length <= maxLen) return subject;
    return subject.substring(0, maxLen) + '...';
  };

  const CHART_COLORS = ['#2D5A27', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722', '#E91E63', '#9C27B0', '#673AB7'];

  if (loading) {
    return (
      <div className="email-portal-container">
        <div className="email-portal-loading">Loading email data...</div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="email-portal-container">
      <style>{styles}</style>

      {/* Header */}
      <div className="email-portal-header">
        <div>
          <h1 className="email-portal-title">Email Portal</h1>
          <p className="email-portal-subtitle">Monitor all outgoing communications</p>
        </div>
        <div className="email-portal-header-buttons">
          {activeTab !== 'campaigns' && (
          <button className="email-portal-btn email-portal-btn-test" onClick={() => setShowTestEmailModal(true)}>
            <TestTube size={16} /> {!isMobile && 'Send Test'}
          </button>
          )}
          <button className="email-portal-btn email-portal-btn-refresh" onClick={() => { loadAnalytics(); loadEmails(); }}>
            <RefreshCw size={16} /> {!isMobile && 'Refresh'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '1.25rem', borderBottom: '2px solid #eee' }}>
        {([
          { key: 'dashboard' as const, label: 'Dashboard' },
          { key: 'campaigns' as const, label: 'Campaigns' },
          { key: 'log' as const, label: 'Email Log' },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.6rem 1.25rem',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #2D5A27' : '2px solid transparent',
              background: 'none',
              color: activeTab === tab.key ? '#2D5A27' : '#666',
              fontWeight: activeTab === tab.key ? 600 : 400,
              cursor: 'pointer',
              fontSize: '0.95rem',
              marginBottom: '-2px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <div className="email-portal-error">{error}</div>}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div>
          {campaignView === 'list' && (
            <CampaignList
              onSelect={(id) => { setSelectedCampaignId(id); setCampaignView('detail'); }}
              onCompose={() => { setSelectedCampaignId(null); setCampaignView('compose'); }}
            />
          )}
          {campaignView === 'compose' && (
            <CampaignCompose
              campaignId={selectedCampaignId}
              onBack={() => setCampaignView('list')}
              onSaved={(id) => setSelectedCampaignId(id)}
            />
          )}
          {campaignView === 'detail' && selectedCampaignId && (
            <CampaignDetail
              campaignId={selectedCampaignId}
              onBack={() => setCampaignView('list')}
              onEdit={(id) => { setSelectedCampaignId(id); setCampaignView('compose'); }}
            />
          )}
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && <>
      {/* Analytics Cards */}
      {analytics && (
        <div className="email-portal-stats-grid">
          <div className="email-portal-stat-card">
            <div className="email-portal-stat-icon"><Mail size={isMobile ? 20 : 24} color="#2D5A27" /></div>
            <div className="email-portal-stat-value">{analytics.totals.last_30_days.toLocaleString()}</div>
            <div className="email-portal-stat-label">{isMobile ? 'Sent' : 'Total Sent (30 days)'}</div>
          </div>
          <div className="email-portal-stat-card">
            <div className="email-portal-stat-icon"><Eye size={isMobile ? 20 : 24} color="#9c27b0" /></div>
            <div className="email-portal-stat-value">{analytics.rates.open_rate}%</div>
            <div className="email-portal-stat-label">{isMobile ? 'Opens' : 'Open Rate'}</div>
          </div>
          <div className="email-portal-stat-card">
            <div className="email-portal-stat-icon"><MousePointer size={isMobile ? 20 : 24} color="#00bcd4" /></div>
            <div className="email-portal-stat-value">{analytics.rates.click_rate}%</div>
            <div className="email-portal-stat-label">{isMobile ? 'Clicks' : 'Click Rate'}</div>
          </div>
          <div className="email-portal-stat-card">
            <div className="email-portal-stat-icon"><AlertTriangle size={isMobile ? 20 : 24} color="#ff9800" /></div>
            <div className="email-portal-stat-value">{analytics.rates.bounce_rate}%</div>
            <div className="email-portal-stat-label">{isMobile ? 'Bounce' : 'Bounce Rate'}</div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      {analytics && (
        <div className="email-portal-charts-row">
          {/* Daily Volume Chart */}
          <div className="email-portal-chart-card email-portal-chart-line">
            <h3 className="email-portal-chart-title">{isMobile ? 'Daily Volume' : 'Daily Email Volume (30 Days)'}</h3>
            <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
              <LineChart data={analytics.daily_volume}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: isMobile ? 9 : 11 }} tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} />
                <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                <Line type="monotone" dataKey="count" stroke="#2D5A27" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Emails by Type */}
          <div className="email-portal-chart-card email-portal-chart-pie">
            <h3 className="email-portal-chart-title">Emails by Type</h3>
            <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
              <PieChart>
                <Pie
                  data={analytics.emails_by_type.slice(0, 8)}
                  dataKey="count"
                  nameKey="type_display"
                  cx="50%"
                  cy="50%"
                  outerRadius={isMobile ? 60 : 80}
                  label={isMobile ? false : ({ name, percent }) => `${name || ''} (${((percent || 0) * 100).toFixed(0)}%)`}
                  labelLine={!isMobile}
                >
                  {analytics.emails_by_type.slice(0, 8).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Alert Testing Section */}
      <div className="email-portal-alert-section">
        <button
          className="email-portal-alert-toggle"
          onClick={() => setShowAlertTesting(!showAlertTesting)}
        >
          <Bell size={18} />
          <span>Automated Alert Testing</span>
          <span className="email-portal-toggle-arrow">{showAlertTesting ? '▼' : '▶'}</span>
        </button>

        {showAlertTesting && (
          <div className="email-portal-alert-content">
            <p className="email-portal-alert-help">
              Send test versions of automated alerts to verify they're working correctly.
            </p>
            <div className="email-portal-alert-form">
              <div className="email-portal-alert-row">
                <label>Alert Type</label>
                <select
                  value={alertType}
                  onChange={(e) => setAlertType(e.target.value as 'feed_bin' | 'weather')}
                  className="email-portal-alert-select"
                >
                  <option value="feed_bin">Feed Bin Low Stock Alert</option>
                  <option value="weather">Weather Alert</option>
                </select>
              </div>
              <div className="email-portal-alert-row">
                <label>Send Test To</label>
                <input
                  type="email"
                  value={alertTestEmail}
                  onChange={(e) => setAlertTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="email-portal-alert-input"
                />
              </div>
              <button
                onClick={handleSendAlertTest}
                disabled={sendingAlertTest}
                className="email-portal-btn email-portal-btn-alert"
              >
                {sendingAlertTest ? 'Sending...' : '📧 Send Test Alert'}
              </button>
              {alertTestResult && (
                <div className={`email-portal-alert-result ${alertTestResult.startsWith('✅') ? 'success' : alertTestResult.startsWith('⚠️') ? 'warning' : 'error'}`}>
                  {alertTestResult}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </>}

      {/* Email Log Tab */}
      {activeTab === 'log' && <>
      {/* Filter Bar */}
      <div className="email-portal-filter-bar">
        <div className="email-portal-search-wrapper">
          <Search size={18} color="#666" />
          <input
            type="text"
            placeholder={isMobile ? "Search..." : "Search recipient or subject..."}
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            className="email-portal-search-input"
          />
        </div>
        <div className="email-portal-filter-selects">
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="email-portal-select"
          >
            <option value="">All Types</option>
            {filterOptions?.email_types.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="email-portal-select"
          >
            <option value="">All Status</option>
            {filterOptions?.statuses.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="email-portal-table-card email-portal-desktop-only">
        <table className="email-portal-table">
          <thead>
            <tr>
              <th>Recipient</th>
              <th>Subject</th>
              <th>Type</th>
              <th>Status</th>
              <th>Sent</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {emails.map((email) => (
              <tr key={email.id} onClick={() => handleViewEmail(email.id)}>
                <td>
                  <div className="email-portal-recipient-cell">
                    <span className="email-portal-recipient-email">{email.recipient_email}</span>
                    {email.customer_name && (
                      <span className="email-portal-customer-name">{email.customer_name}</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className="email-portal-subject">{email.subject.length > 50 ? email.subject.substring(0, 50) + '...' : email.subject}</span>
                </td>
                <td>
                  <span className="email-portal-type-tag">{getEmailTypeDisplay(email.email_type, email.email_type_display)}</span>
                </td>
                <td>
                  <span className="email-portal-status-badge" style={{ backgroundColor: getStatusColor(email.status) }}>
                    {getStatusIcon(email.status)} {email.status_display}
                    {email.opened_count > 1 && ` (${email.opened_count}x)`}
                  </span>
                </td>
                <td>
                  <span className="email-portal-date-text">{formatDate(email.sent_at)}</span>
                </td>
                <td>
                  <button className="email-portal-view-btn" onClick={(e) => { e.stopPropagation(); handleViewEmail(email.id); }}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {emails.length === 0 && (
          <div className="email-portal-no-data">No emails found</div>
        )}
      </div>

      {/* Mobile Card List */}
      <div className="email-portal-mobile-list email-portal-mobile-only">
        {emails.map((email) => (
          <div key={email.id} className="email-portal-mobile-card" onClick={() => handleViewEmail(email.id)}>
            <div className="email-portal-mobile-card-top">
              <span className="email-portal-mobile-recipient">{truncateEmail(email.recipient_email)}</span>
              <span className="email-portal-mobile-time">{formatDate(email.sent_at)}</span>
            </div>
            <div className="email-portal-mobile-subject">{truncateSubject(email.subject, 60)}</div>
            <div className="email-portal-mobile-card-bottom">
              <span className="email-portal-type-tag email-portal-type-tag-small">{getEmailTypeDisplay(email.email_type, email.email_type_display)}</span>
              <span className="email-portal-status-badge email-portal-status-badge-small" style={{ backgroundColor: getStatusColor(email.status) }}>
                {getStatusIcon(email.status)} {email.status_display}
              </span>
            </div>
          </div>
        ))}
        {emails.length === 0 && (
          <div className="email-portal-no-data">No emails found</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="email-portal-pagination">
          <button
            className="email-portal-page-btn"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          <span className="email-portal-page-info">{page} / {totalPages}</span>
          <button
            className="email-portal-page-btn"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
      </>}

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="email-portal-modal-overlay" onClick={() => setShowTestEmailModal(false)}>
          <div className="email-portal-test-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-portal-test-modal-header">
              <h3>Send Test Email</h3>
              <button className="email-portal-close-btn" onClick={() => setShowTestEmailModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="email-portal-test-modal-body">
              <label className="email-portal-test-label">Email Address</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address..."
                className="email-portal-test-input"
                autoFocus
              />
            </div>
            <div className="email-portal-test-modal-footer">
              <button className="email-portal-btn email-portal-btn-cancel" onClick={() => setShowTestEmailModal(false)}>
                Cancel
              </button>
              <button
                className="email-portal-btn email-portal-btn-send"
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail || !testEmail}
              >
                {sendingTestEmail ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Detail Modal */}
      {showModal && selectedEmail && (
        <div className="email-portal-modal-overlay" onClick={() => setShowModal(false)}>
          <div className={`email-portal-modal ${isMobile ? 'email-portal-modal-fullscreen' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="email-portal-modal-header">
              <h2 className="email-portal-modal-title">Email Details</h2>
              <button className="email-portal-close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="email-portal-modal-content">
              {/* Email Info */}
              <div className="email-portal-email-info">
                <div className="email-portal-info-row">
                  <span className="email-portal-info-label">To:</span>
                  <span className="email-portal-info-value">{selectedEmail.recipient_email}</span>
                </div>
                <div className="email-portal-info-row">
                  <span className="email-portal-info-label">Subject:</span>
                  <span className="email-portal-info-value">{selectedEmail.subject}</span>
                </div>
                <div className="email-portal-info-row">
                  <span className="email-portal-info-label">Type:</span>
                  <span className="email-portal-info-value">{getEmailTypeDisplay(selectedEmail.email_type, selectedEmail.email_type_display)}</span>
                </div>
                <div className="email-portal-info-row">
                  <span className="email-portal-info-label">Status:</span>
                  <span className="email-portal-status-badge" style={{ backgroundColor: getStatusColor(selectedEmail.status) }}>
                    {selectedEmail.status_display}
                    {selectedEmail.opened_count > 0 && ` (opened ${selectedEmail.opened_count}x)`}
                  </span>
                </div>
                <div className="email-portal-info-row">
                  <span className="email-portal-info-label">Sent:</span>
                  <span className="email-portal-info-value">{new Date(selectedEmail.sent_at).toLocaleString()}</span>
                </div>
                {selectedEmail.opened_at && (
                  <div className="email-portal-info-row">
                    <span className="email-portal-info-label">Opened:</span>
                    <span className="email-portal-info-value">{new Date(selectedEmail.opened_at).toLocaleString()}</span>
                  </div>
                )}
                {selectedEmail.clicked_at && (
                  <div className="email-portal-info-row">
                    <span className="email-portal-info-label">Clicked:</span>
                    <span className="email-portal-info-value">{new Date(selectedEmail.clicked_at).toLocaleString()} ({selectedEmail.clicked_count} clicks)</span>
                  </div>
                )}
                {selectedEmail.customer_name && (
                  <div className="email-portal-info-row">
                    <span className="email-portal-info-label">Customer:</span>
                    <a href={`/customers/${selectedEmail.customer_id}`} className="email-portal-link-value">
                      {selectedEmail.customer_name} <ExternalLink size={12} />
                    </a>
                  </div>
                )}
                {selectedEmail.order_number && (
                  <div className="email-portal-info-row">
                    <span className="email-portal-info-label">Order:</span>
                    <a href={`/orders?search=${selectedEmail.order_number}`} className="email-portal-link-value">
                      #{selectedEmail.order_number} <ExternalLink size={12} />
                    </a>
                  </div>
                )}
                {selectedEmail.error_message && (
                  <div className="email-portal-error-box">
                    <strong>Error:</strong> {selectedEmail.error_message}
                  </div>
                )}
              </div>

              {/* Tracking Timeline */}
              <div className="email-portal-tracking-timeline">
                <h4 className="email-portal-tracking-title">Tracking Timeline</h4>
                <div className="email-portal-timeline-steps">
                  {/* Sent */}
                  <div className="email-portal-timeline-step">
                    <span style={{ color: '#4caf50' }}>✓</span>
                    <span className="email-portal-timeline-label">Sent</span>
                    <span className="email-portal-timeline-date">{new Date(selectedEmail.sent_at).toLocaleString()}</span>
                  </div>

                  {/* Delivered */}
                  {selectedEmail.status !== 'FAILED' && selectedEmail.status !== 'BOUNCED' && (
                    <div className="email-portal-timeline-step">
                      <span style={{ color: selectedEmail.delivered_at ? '#4caf50' : '#9e9e9e' }}>
                        {selectedEmail.delivered_at ? '✓' : '○'}
                      </span>
                      <span className="email-portal-timeline-label">Delivered</span>
                      <span className="email-portal-timeline-date">
                        {selectedEmail.delivered_at ? new Date(selectedEmail.delivered_at).toLocaleString() : 'Pending...'}
                      </span>
                    </div>
                  )}

                  {/* Opened */}
                  {selectedEmail.opened_at && (
                    <div className="email-portal-timeline-step">
                      <span style={{ color: '#9c27b0' }}>👁</span>
                      <span className="email-portal-timeline-label">Opened</span>
                      <span className="email-portal-timeline-date">
                        {new Date(selectedEmail.opened_at).toLocaleString()}
                        {selectedEmail.opened_count > 1 && ` (${selectedEmail.opened_count}x)`}
                      </span>
                    </div>
                  )}

                  {/* Clicked */}
                  {selectedEmail.clicked_at && (
                    <div className="email-portal-timeline-step">
                      <span style={{ color: '#00bcd4' }}>👆</span>
                      <span className="email-portal-timeline-label">Clicked</span>
                      <span className="email-portal-timeline-date">
                        {new Date(selectedEmail.clicked_at).toLocaleString()}
                        {selectedEmail.clicked_count > 1 && ` (${selectedEmail.clicked_count}x)`}
                      </span>
                    </div>
                  )}

                  {/* Clicked Links */}
                  {selectedEmail.clicked_links && selectedEmail.clicked_links.length > 0 && (
                    <div className="email-portal-clicked-links">
                      {selectedEmail.clicked_links.map((link: ClickedLink, i: number) => (
                        <div key={i} className="email-portal-clicked-link">
                          ↳ {typeof link === 'string' ? link : link.url}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bounced */}
                  {selectedEmail.status === 'BOUNCED' && (
                    <div className="email-portal-timeline-step">
                      <span style={{ color: '#ff9800' }}>⚠️</span>
                      <span className="email-portal-timeline-label">Bounced</span>
                      <span className="email-portal-timeline-date">{selectedEmail.bounce_type || 'Unknown'}</span>
                    </div>
                  )}

                  {/* Unsubscribed */}
                  {selectedEmail.unsubscribed_at && (
                    <div className="email-portal-timeline-step">
                      <span style={{ color: '#795548' }}>🚫</span>
                      <span className="email-portal-timeline-label">Unsubscribed</span>
                      <span className="email-portal-timeline-date">{new Date(selectedEmail.unsubscribed_at).toLocaleString()}</span>
                    </div>
                  )}

                  {/* Complained */}
                  {selectedEmail.status === 'COMPLAINED' && (
                    <div className="email-portal-timeline-step">
                      <span style={{ color: '#d32f2f' }}>🚨</span>
                      <span className="email-portal-timeline-label">Marked as Spam</span>
                      <span className="email-portal-timeline-date">Reported by recipient</span>
                    </div>
                  )}

                  {/* Failed */}
                  {selectedEmail.status === 'FAILED' && (
                    <div className="email-portal-timeline-step">
                      <span style={{ color: '#f44336' }}>✗</span>
                      <span className="email-portal-timeline-label">Failed</span>
                      <span className="email-portal-timeline-date">{selectedEmail.error_message || 'Delivery failed'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="email-portal-modal-actions">
                <button
                  className={`email-portal-resend-btn ${!selectedEmail.html_content ? 'email-portal-resend-btn-disabled' : ''}`}
                  onClick={handleResend}
                  disabled={resending || !selectedEmail.html_content}
                  title={!selectedEmail.html_content ? 'Cannot resend - content not stored' : 'Resend this email'}
                >
                  <Send size={16} /> {resending ? 'Sending...' : 'Resend Email'}
                </button>
                {!selectedEmail.html_content && (
                  <span className="email-portal-resend-note">Content not stored (older email)</span>
                )}
              </div>

              {/* Email Preview */}
              {selectedEmail.html_content && (
                <div className="email-portal-preview-section">
                  <h3 className="email-portal-preview-title">Email Preview</h3>
                  <iframe
                    srcDoc={selectedEmail.html_content}
                    className="email-portal-preview-frame"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              )}

              {!selectedEmail.html_content && (
                <div className="email-portal-no-preview">
                  Email content not available for preview
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = `
  .email-portal-container {
    padding: 1rem;
    max-width: 1400px;
    margin: 0 auto;
    background-color: #f5f5f5;
    min-height: 100vh;
  }

  @media (min-width: 768px) {
    .email-portal-container {
      padding: 2rem;
    }
  }

  .email-portal-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
    font-size: 1rem;
    color: #666;
  }

  .email-portal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  @media (min-width: 768px) {
    .email-portal-header {
      margin-bottom: 1.5rem;
    }
  }

  .email-portal-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;
    margin: 0;
  }

  @media (min-width: 768px) {
    .email-portal-title {
      font-size: 1.75rem;
    }
  }

  .email-portal-subtitle {
    font-size: 0.8rem;
    color: #666;
    margin-top: 0.25rem;
  }

  .email-portal-header-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .email-portal-btn {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;
    transition: background-color 0.15s;
  }

  .email-portal-btn-refresh {
    background-color: #fff;
    border: 1px solid #ddd;
    color: #333;
  }

  .email-portal-btn-refresh:hover {
    background-color: #f5f5f5;
  }

  .email-portal-btn-test {
    background-color: #2D5A27;
    border: 1px solid #2D5A27;
    color: #fff;
  }

  .email-portal-btn-test:hover {
    background-color: #234a1f;
  }

  .email-portal-error {
    background-color: #ffebee;
    color: #c62828;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  /* Stats Grid - 2x2 on mobile, 4 across on desktop */
  .email-portal-stats-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  @media (min-width: 768px) {
    .email-portal-stats-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
  }

  .email-portal-stat-card {
    background-color: #fff;
    padding: 0.875rem;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    text-align: center;
  }

  @media (min-width: 768px) {
    .email-portal-stat-card {
      padding: 1.25rem;
      border-radius: 12px;
    }
  }

  .email-portal-stat-icon {
    margin-bottom: 0.375rem;
  }

  .email-portal-stat-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #333;
  }

  @media (min-width: 768px) {
    .email-portal-stat-value {
      font-size: 1.75rem;
    }
  }

  .email-portal-stat-label {
    font-size: 0.7rem;
    color: #666;
    margin-top: 0.125rem;
  }

  @media (min-width: 768px) {
    .email-portal-stat-label {
      font-size: 0.8rem;
      margin-top: 0.25rem;
    }
  }

  /* Charts - Stack on mobile, side by side on desktop */
  .email-portal-charts-row {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  @media (min-width: 768px) {
    .email-portal-charts-row {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
  }

  .email-portal-chart-card {
    background-color: #fff;
    padding: 1rem;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  @media (min-width: 768px) {
    .email-portal-chart-card {
      padding: 1.25rem;
      border-radius: 12px;
    }
  }

  .email-portal-chart-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.75rem;
  }

  @media (min-width: 768px) {
    .email-portal-chart-title {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }

  /* Filter Bar */
  .email-portal-filter-bar {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
    background-color: #fff;
    padding: 0.875rem;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  @media (min-width: 768px) {
    .email-portal-filter-bar {
      flex-direction: row;
      padding: 1rem;
      border-radius: 12px;
    }
  }

  .email-portal-search-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    background-color: #f5f5f5;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
  }

  .email-portal-search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 0.875rem;
    outline: none;
    min-width: 0;
  }

  .email-portal-filter-selects {
    display: flex;
    gap: 0.5rem;
  }

  .email-portal-select {
    flex: 1;
    padding: 0.5rem 0.75rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.8rem;
    background-color: #fff;
    cursor: pointer;
    min-width: 0;
  }

  @media (min-width: 768px) {
    .email-portal-select {
      flex: initial;
      font-size: 0.9rem;
      padding: 0.5rem 1rem;
    }
  }

  /* Desktop Table */
  .email-portal-desktop-only {
    display: none;
  }

  @media (min-width: 768px) {
    .email-portal-desktop-only {
      display: block;
    }
  }

  .email-portal-table-card {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    overflow: hidden;
  }

  .email-portal-table {
    width: 100%;
    border-collapse: collapse;
  }

  .email-portal-table th {
    padding: 1rem;
    text-align: left;
    background-color: #f9f9f9;
    border-bottom: 1px solid #eee;
    font-size: 0.8rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
  }

  .email-portal-table tr {
    border-bottom: 1px solid #eee;
    cursor: pointer;
    transition: background-color 0.15s;
  }

  .email-portal-table tr:hover {
    background-color: #fafafa;
  }

  .email-portal-table td {
    padding: 1rem;
    font-size: 0.9rem;
    vertical-align: middle;
  }

  .email-portal-recipient-cell {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .email-portal-recipient-email {
    font-weight: 500;
    color: #333;
  }

  .email-portal-customer-name {
    font-size: 0.8rem;
    color: #666;
  }

  .email-portal-subject {
    color: #333;
  }

  .email-portal-type-tag {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    background-color: #e8f5e9;
    color: #2D5A27;
    border-radius: 4px;
    white-space: nowrap;
  }

  .email-portal-type-tag-small {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }

  .email-portal-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    color: #fff;
    white-space: nowrap;
  }

  .email-portal-status-badge-small {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }

  .email-portal-date-text {
    color: #666;
    font-size: 0.85rem;
  }

  .email-portal-view-btn {
    padding: 0.375rem 0.75rem;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .email-portal-view-btn:hover {
    background-color: #eee;
  }

  .email-portal-no-data {
    padding: 3rem;
    text-align: center;
    color: #666;
  }

  /* Mobile Card List */
  .email-portal-mobile-only {
    display: block;
  }

  @media (min-width: 768px) {
    .email-portal-mobile-only {
      display: none;
    }
  }

  .email-portal-mobile-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .email-portal-mobile-card {
    background-color: #fff;
    padding: 0.875rem;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    cursor: pointer;
    transition: background-color 0.15s;
  }

  .email-portal-mobile-card:active {
    background-color: #f9f9f9;
  }

  .email-portal-mobile-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.375rem;
  }

  .email-portal-mobile-recipient {
    font-weight: 500;
    color: #333;
    font-size: 0.875rem;
  }

  .email-portal-mobile-time {
    font-size: 0.75rem;
    color: #888;
  }

  .email-portal-mobile-subject {
    color: #555;
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .email-portal-mobile-card-bottom {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  /* Pagination */
  .email-portal-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    margin-top: 0.5rem;
  }

  .email-portal-page-btn {
    padding: 0.5rem 1rem;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    min-width: 44px;
    min-height: 44px;
  }

  .email-portal-page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .email-portal-page-info {
    color: #666;
    font-size: 0.9rem;
  }

  /* Modal Overlay */
  .email-portal-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 0;
  }

  @media (min-width: 768px) {
    .email-portal-modal-overlay {
      padding: 2rem;
    }
  }

  /* Main Modal */
  .email-portal-modal {
    background-color: #fff;
    border-radius: 0;
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    overflow: auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  }

  @media (min-width: 768px) {
    .email-portal-modal {
      border-radius: 16px;
      max-width: 900px;
      max-height: 90vh;
      height: auto;
    }
  }

  .email-portal-modal-fullscreen {
    border-radius: 0;
  }

  .email-portal-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #eee;
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 1;
  }

  @media (min-width: 768px) {
    .email-portal-modal-header {
      padding: 1.25rem 1.5rem;
    }
  }

  .email-portal-modal-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
  }

  @media (min-width: 768px) {
    .email-portal-modal-title {
      font-size: 1.25rem;
    }
  }

  .email-portal-close-btn {
    padding: 0.5rem;
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .email-portal-modal-content {
    padding: 1rem;
  }

  @media (min-width: 768px) {
    .email-portal-modal-content {
      padding: 1.5rem;
    }
  }

  .email-portal-email-info {
    margin-bottom: 1.25rem;
  }

  .email-portal-info-row {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
  }

  @media (min-width: 768px) {
    .email-portal-info-row {
      flex-direction: row;
      margin-bottom: 0.75rem;
    }
  }

  .email-portal-info-label {
    width: 100%;
    font-weight: 500;
    color: #666;
    flex-shrink: 0;
    margin-bottom: 0.125rem;
    font-size: 0.8rem;
  }

  @media (min-width: 768px) {
    .email-portal-info-label {
      width: 100px;
      margin-bottom: 0;
      font-size: inherit;
    }
  }

  .email-portal-info-value {
    color: #333;
    word-break: break-word;
  }

  .email-portal-link-value {
    color: #2D5A27;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  .email-portal-error-box {
    background-color: #ffebee;
    color: #c62828;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-top: 0.5rem;
    font-size: 0.875rem;
  }

  /* Tracking Timeline */
  .email-portal-tracking-timeline {
    margin-top: 1.25rem;
    padding: 1rem;
    background-color: #fafafa;
    border-radius: 8px;
    border: 1px solid #eee;
  }

  .email-portal-tracking-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .email-portal-timeline-steps {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }

  .email-portal-timeline-step {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    font-size: 0.8rem;
  }

  @media (min-width: 768px) {
    .email-portal-timeline-step {
      align-items: center;
      font-size: 0.875rem;
    }
  }

  .email-portal-timeline-label {
    font-weight: 500;
    min-width: 80px;
  }

  @media (min-width: 768px) {
    .email-portal-timeline-label {
      min-width: 100px;
    }
  }

  .email-portal-timeline-date {
    color: #666;
    font-size: 0.75rem;
  }

  @media (min-width: 768px) {
    .email-portal-timeline-date {
      font-size: 0.8rem;
    }
  }

  .email-portal-clicked-links {
    margin-left: 1.5rem;
    font-size: 0.7rem;
    color: #666;
  }

  @media (min-width: 768px) {
    .email-portal-clicked-links {
      margin-left: 2rem;
      font-size: 0.75rem;
    }
  }

  .email-portal-clicked-link {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 300px;
  }

  @media (min-width: 768px) {
    .email-portal-clicked-link {
      max-width: 400px;
    }
  }

  /* Modal Actions */
  .email-portal-modal-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;
    align-items: center;
  }

  .email-portal-resend-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background-color: #2D5A27;
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;
    min-height: 44px;
  }

  .email-portal-resend-btn:hover:not(:disabled) {
    background-color: #234a1f;
  }

  .email-portal-resend-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .email-portal-resend-btn-disabled {
    background-color: #9e9e9e;
  }

  .email-portal-resend-note {
    font-size: 0.75rem;
    color: #888;
    font-style: italic;
  }

  /* Preview */
  .email-portal-preview-section {
    border-top: 1px solid #eee;
    padding-top: 1.25rem;
  }

  .email-portal-preview-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .email-portal-preview-frame {
    width: 100%;
    height: 350px;
    border: 1px solid #eee;
    border-radius: 8px;
    background-color: #fff;
  }

  @media (min-width: 768px) {
    .email-portal-preview-frame {
      height: 500px;
    }
  }

  .email-portal-no-preview {
    padding: 2rem;
    text-align: center;
    color: #666;
    background-color: #f5f5f5;
    border-radius: 8px;
  }

  /* Test Email Modal */
  .email-portal-test-modal {
    background-color: #fff;
    border-radius: 12px;
    width: 90%;
    max-width: 400px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  }

  .email-portal-test-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #eee;
  }

  .email-portal-test-modal-header h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .email-portal-test-modal-body {
    padding: 1.25rem 1rem;
  }

  .email-portal-test-label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: #666;
    margin-bottom: 0.5rem;
  }

  .email-portal-test-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    box-sizing: border-box;
  }

  .email-portal-test-input:focus {
    border-color: #2D5A27;
  }

  .email-portal-test-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem;
    border-top: 1px solid #eee;
  }

  .email-portal-btn-cancel {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    color: #333;
  }

  .email-portal-btn-cancel:hover {
    background-color: #eee;
  }

  .email-portal-btn-send {
    background-color: #2D5A27;
    border: 1px solid #2D5A27;
    color: #fff;
  }

  .email-portal-btn-send:hover:not(:disabled) {
    background-color: #234a1f;
  }

  .email-portal-btn-send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Alert Testing Section */
  .email-portal-alert-section {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    margin-bottom: 1rem;
    overflow: hidden;
  }

  @media (min-width: 768px) {
    .email-portal-alert-section {
      border-radius: 12px;
      margin-bottom: 1.5rem;
    }
  }

  .email-portal-alert-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.875rem 1rem;
    background-color: #fff;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    text-align: left;
    transition: background-color 0.15s;
  }

  .email-portal-alert-toggle:hover {
    background-color: #f9f9f9;
  }

  @media (min-width: 768px) {
    .email-portal-alert-toggle {
      padding: 1rem 1.25rem;
      font-size: 1rem;
    }
  }

  .email-portal-toggle-arrow {
    margin-left: auto;
    color: #666;
    font-size: 0.75rem;
  }

  .email-portal-alert-content {
    padding: 0 1rem 1rem;
    border-top: 1px solid #eee;
  }

  @media (min-width: 768px) {
    .email-portal-alert-content {
      padding: 0 1.25rem 1.25rem;
    }
  }

  .email-portal-alert-help {
    font-size: 0.85rem;
    color: #666;
    margin: 0.875rem 0;
    line-height: 1.4;
  }

  .email-portal-alert-form {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  @media (min-width: 768px) {
    .email-portal-alert-form {
      flex-direction: row;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: 1rem;
    }
  }

  .email-portal-alert-row {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  @media (min-width: 768px) {
    .email-portal-alert-row {
      flex: 1;
      min-width: 180px;
    }
  }

  .email-portal-alert-row label {
    font-size: 0.8rem;
    font-weight: 500;
    color: #666;
  }

  .email-portal-alert-select,
  .email-portal-alert-input {
    padding: 0.625rem 0.75rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.875rem;
    background-color: #fff;
    width: 100%;
    box-sizing: border-box;
  }

  .email-portal-alert-select:focus,
  .email-portal-alert-input:focus {
    outline: none;
    border-color: #2D5A27;
  }

  .email-portal-btn-alert {
    background-color: #f59e0b;
    border: 1px solid #f59e0b;
    color: #fff;
    padding: 0.625rem 1rem;
    min-height: 44px;
    white-space: nowrap;
  }

  .email-portal-btn-alert:hover:not(:disabled) {
    background-color: #d97706;
  }

  .email-portal-btn-alert:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    .email-portal-btn-alert {
      flex-shrink: 0;
    }
  }

  .email-portal-alert-result {
    padding: 0.625rem 0.875rem;
    border-radius: 6px;
    font-size: 0.85rem;
    line-height: 1.4;
  }

  @media (min-width: 768px) {
    .email-portal-alert-result {
      width: 100%;
    }
  }

  .email-portal-alert-result.success {
    background-color: #d1fae5;
    color: #065f46;
  }

  .email-portal-alert-result.warning {
    background-color: #fef3c7;
    color: #92400e;
  }

  .email-portal-alert-result.error {
    background-color: #fee2e2;
    color: #991b1b;
  }
`;

export default EmailPortal;
